// src/Services/attendance.service.js
import config from "../config";
import selectedEnvironment from "../env";

const API_URL = `${config[selectedEnvironment].API_URL}/Attendance`;

/**
 * Fetch attendance records with filters.
 * @param {{ accountantId?: string, userIds?: string[], startUtc: string, endUtc: string }} payload
 * @param {string} authToken
 */
const GetFilteredAttendance = async (accountantId, payload, authToken) => {
    const { subAccountantIds = [], clientCaseIds = [], ...rest } = payload;
    const userIds = subAccountantIds.map((sa) => sa.value);
    const body = {
        ...rest,
        accountantId,
        userIds,
    };

    const response = await fetch(`${API_URL}/filter`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
    });

    return response;
};


const CheckIn = async (accountantId, authToken, clientTimeLocal) => {
    const response = await fetch(`${API_URL}/enter`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },

        body: JSON.stringify({ clientTimeLocal })
    });
    return response;
};

/**
 * Check-out for the current user, sending the client’s timestamp.
 */
const CheckOut = async (accountantId, authToken, clientTimeLocal) => {
    const response = await fetch(`${API_URL}/exit`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ clientTimeLocal })
    });
    return response;
};


const CheckStatus = async ( authToken, clientTimeLocal) => {
    
    const response = await fetch(`${API_URL}/status`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ clientTimeLocal })
    });
    return response;
};

/**
 * Download the monthly attendance report in HTML format
 * @param {string} accountantId
 * @param {Object} filter
 * @param {number} year
 * @param {number} month
 * @param {string} authToken
 * @returns {Promise<string>} - HTML string
 */
const DownloadMonthlyReportHtml = async (accountantId, filter, year, month, authToken) => {
    const body = {
        accountantId,
        filter: {
            userIds: filter.subAccountantIds.map(s => s.value),
        },
        year,
        month,
    };

    return await fetch(`${API_URL}/report/pdf`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
    }); // Return full response for blob usage
};


const GetMonthlySchedule = async (year, month, authToken, userId = null) => {
    const url = userId 
        ? `${API_URL}/monthly-schedule?year=${year}&month=${month}&userId=${userId}`
        : `${API_URL}/monthly-schedule?year=${year}&month=${month}`;
    
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
    });
    return response;
};

const UpdateDayRemarks = async (year, month, day, remarks, authToken, userId = null) => {
    const response = await fetch(`${API_URL}/update-day-remarks`, {
        method: "PUT",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ year, month, day, remarks, userId })
    });
    return response;
};

const UpdateAttendanceEntry = async (entryId, checkInTime, checkOutTime, remarks, authToken) => {
    const response = await fetch(`${API_URL}/update-entry`, {
        method: "PUT",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
            entryId, 
            checkInTime, 
            checkOutTime, 
            remarks 
        })
    });
    return response;
};

const CreateAttendance = async (attendanceData, authToken) => {
    const response = await fetch(`${API_URL}/create`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(attendanceData)
    });
    return response;
};

const GenerateMonthlyReportHtml = async (authToken, filter, year, month) => {
    const response = await fetch(`${API_URL}/generate-monthly-report`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ filter, year, month })
    });
    return response;
};

/**
 * Export detailed PDF report for attendance
 * @param {string} authToken
 * @param {Object} filter
 * @param {number} year
 * @param {number} month
 * @returns {Promise<Response>}
 */
const ExportDetailedPdfReport = async (authToken, filter, accountantId, year, month) => {
    const response = await fetch(`${API_URL}/report/pdf-detailed`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            filter,
            year,
            month,
            accountantId
        })
    });
    return response;
};

/**
 * Export Excel report for attendance
 * @param {string} authToken
 * @param {Object} filter
 * @param {number} year
 * @param {number} month
 * @returns {Promise<Response>}
 */
const ExportExcelReport = async (authToken, filter, accountantId, year, month) => {
    const response = await fetch(`${API_URL}/report/excel`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            filter,
            year,
            month,
            accountantId
        })
    });
    return response;
};

const DeleteAttendanceEntry = async (entryId, authToken) => {
    const response = await fetch(`${API_URL}/${entryId}`, {
        method: "DELETE",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        }
    });
    return response;
};

const SaveLinkedAssignments = async (year, month, day, linkedAssignments, authToken) => {
    const response = await fetch(`${API_URL}/linked-assignments`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            year,
            month,
            day,
            linkedAssignments
        })
    });
    return response;
};

const ExportDetailedPdfReportForAllUsers = async (authToken, filter, accountantId, year, month) => {
    const response = await fetch(`${API_URL}/report/pdf-detailed-all-users`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            filter,
            year,
            month,
            accountantId
        })
    });
    return response;
};

const ExportExcelReportForAllUsers = async (authToken, filter, accountantId, year, month) => {
    const response = await fetch(`${API_URL}/report/excel-all-users`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            filter,
            year,
            month,
            accountantId
        })
    });
    return response;
};

const FixEntriesWithBothTimes = async (authToken) => {
    const response = await fetch(`${API_URL}/fix-entries-with-both-times`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        }
    });
    return response;
};

const AttendanceService = {
    GetFilteredAttendance,
    CheckIn,
    CheckOut,
    CheckStatus,
    DownloadMonthlyReportHtml,
    GetMonthlySchedule,
    UpdateDayRemarks,
    UpdateAttendanceEntry,
    CreateAttendance,
    GenerateMonthlyReportHtml,
    ExportDetailedPdfReport,
    ExportExcelReport,
    DeleteAttendanceEntry,
    SaveLinkedAssignments,
    ExportDetailedPdfReportForAllUsers,
    ExportExcelReportForAllUsers,
    FixEntriesWithBothTimes
};

export default AttendanceService;
